import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { space } from 'styled-system';

import { RenderingType } from '../../../../product-mvc-buttons/components/enums';
import type { GroupValue as GroupValueItem } from '../../../../product-mvc-buttons/components/types';
import type { SpaceProps } from '../../../theme/system-types';
import { Flex } from '../../Flex';
import RadioCard from '../../Radio/RadioCard';
import { BodyBoldBB1 } from '../../Typography';
import GroupValueArticle from '../GroupValueArticle';
import type { ChangeHandler, SelectedValue } from '../types';
import { useGroupValue } from './hooks';
import Image from './Image';
import NotAvailableLabel from './NotAvailableLabel';

interface GroupValueProps {
  sku: string;
  url: string;
  variationKey: Nullable<string>;
  groupValue: GroupValueItem;
  renderingType: RenderingType;
  selectedValue: Nullable<SelectedValue>;
  listLength: number;
  onChange: ChangeHandler;
  isVariationDrawer?: boolean;
}

const Container = styled.li`
  ${space};
`;
const containerStyles: SpaceProps = {
  p: 'sp_0',
  mb: ['sp_4', null, null, 'sp_8'],
};

const RadioCardContainer = styled(RadioCard)<SpaceProps>`
  ${space};
`;
const radioCardContainerStyles: SpaceProps = {
  p: ['sp_12', null, null, 'sp_16'],
};

const GroupValue = ({
  sku: articleSku,
  url: articleUrl,
  groupValue: { article, ...groupValue },
  renderingType,
  isVariationDrawer,
  ...props
}: GroupValueProps) => {
  const { title, imageUrl, disabled, hasAlternatives } = groupValue;
  const isAvailable = !disabled || hasAlternatives;
  const isOptionIcon = renderingType === RenderingType.Icon;
  const { value, isSelected, handleChange } = useGroupValue({
    ...props,
    articleSku,
    articleUrl,
    groupValue,
  });

  return (
    <Container {...containerStyles}>
      <RadioCardContainer
        {...radioCardContainerStyles}
        position="right"
        value={'url' in value ? value.url : value.alternativeVariantValue}
        name="product-mvc-buttons"
        label={title}
        checked={isSelected}
        disabled={disabled && !hasAlternatives}
        onChange={handleChange}
        aria-labelledby="current-price delivery-time-period"
      >
        <Flex>
          {isOptionIcon && (
            <Flex mr={['sp_8', null, 'sp_12']} alignItems="center">
              <Image
                title=""
                url={imageUrl}
                disabled={isVariationDrawer ? disabled && !hasAlternatives : disabled}
              />
            </Flex>
          )}
          <Flex flexDirection="column">
            <BodyBoldBB1 color="sys.neutral.text.strong">{title}</BodyBoldBB1>
            {isAvailable && <GroupValueArticle article={article} />}
            {disabled && <NotAvailableLabel hasAlternatives={!!hasAlternatives} />}
          </Flex>
        </Flex>
      </RadioCardContainer>
    </Container>
  );
};

export default GroupValue;
